import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../config";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSave, faSpinner, faCog } from "@fortawesome/free-solid-svg-icons";
import { RateLimitAlertContext } from "../contexts/RateLimitAlertContext";
import { AdminAccessAlertContext } from "../contexts/AdminAccessAlertContext";

const CorporateDetails = () => {
  const { corp_code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({
    corporateCode: "",
    corporateName: "",
    corpPrefix: "",
    tinNumber: "",
    corpNameReflectOnSiSoa: "",
    billingAddress: "",
    status_flag: "",
  });
  const [applyToAll, setApplyToAll] = useState(false);
  const [applyTinNumber, setApplyTinNumber] = useState(false);
  const [applyBillingAddress, setApplyBillingAddress] = useState(false);
  const [applyCorpNameReflectOnSiSoa, setApplyCorpNameReflectOnSiSoa] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    corporateName: "",
    tinNumber: "",
    corpNameReflectOnSiSoa: "",
  });

  const { openAlert: openRateLimitAlert } = useContext(RateLimitAlertContext);
  const { openAlert: openAdminAccessAlert } = useContext(AdminAccessAlertContext);

  const statusMap = {
    1: { name: "Active", color: "bg-green-100 text-green-700" },
    2: { name: "Terminated/Cancelled", color: "bg-red-100 text-red-700" },
    3: { name: "Suspended", color: "bg-yellow-100 text-yellow-700" },
    4: { name: "Expired", color: "bg-gray-100 text-gray-700" },
  };

  useEffect(() => {
    fetchData();
  }, [corp_code]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/corporate/${corp_code}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      const corporate = response.data.data;
      setData({
        corporateCode: corporate.corporateCode,
        corporateName: corporate.corporateName,
        corpPrefix: corporate.corpPrefix,
        tinNumber: corporate.tinNumber,
        corpNameReflectOnSiSoa: corporate.corpNameReflectOnSiSoa,
        billingAddress: corporate.billingAddress,
        status_flag: corporate.status_flag,
        actualCorpName: corporate.actualCorpName,
      });
      validateCorporateName(corporate.corporateName);
      validateTinNumber(corporate.tinNumber);
      validateCorpNameReflectOnSiSoa(corporate.corpNameReflectOnSiSoa);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = "/login";
      if (error.response?.status === 403) openAdminAccessAlert();
      if (error.response?.status === 429) openRateLimitAlert();
      if (error.response?.status === 404) {
        Swal.fire({
          title: "Not Found!",
          html: `<div class="text-center text-base leading-6">
                   <p>The corporate record you are looking for does not exist.</p>
                   <p>You will be redirected to the corporate settings page.</p>
                 </div>`,
          icon: "error",
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/corporate-settings");
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const validateCorporateName = (value) => {
    if (!value.trim()) {
      setErrors((prev) => ({ ...prev, corporateName: "Corporate Name is required." }));
      return false;
    } else {
      setErrors((prev) => ({ ...prev, corporateName: "" }));
      return true;
    }
  };

  const validateTinNumber = (value) => {
    if (!value.trim()) {
      setErrors((prev) => ({ ...prev, tinNumber: "TIN Number is required." }));
      return false;
    }
    const tinCleaned = value.replace(/-/g, "");
    if (!/^\d{1,20}$/.test(tinCleaned)) {
      setErrors((prev) => ({
        ...prev,
        tinNumber: "TIN Number must be between 1 and 20 digits.",
      }));
      return false;
    } else {
      setErrors((prev) => ({ ...prev, tinNumber: "" }));
      return true;
    }
  };

  const validateCorpNameReflectOnSiSoa = (value) => {
    if (!value.trim()) {
      setErrors((prev) => ({ ...prev, corpNameReflectOnSiSoa: "Corp Name Reflect on SI/SOA is required." }));
      return false;
    } else if (value.length > 150) {
      setErrors((prev) => ({ ...prev, corpNameReflectOnSiSoa: "Corp Name Reflect on SI/SOA must not exceed 150 characters." }));
      return false;
    } else {
      setErrors((prev) => ({ ...prev, corpNameReflectOnSiSoa: "" }));
      return true;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "tinNumber") {
      const filteredValue = value.replace(/[^0-9-]/g, "");
      setData((prev) => ({ ...prev, [name]: filteredValue }));
      validateTinNumber(filteredValue);
    } else if (name === "corporateName") {
      setData((prev) => ({ ...prev, [name]: value }));
      validateCorporateName(value);
    } else if (name === "corpNameReflectOnSiSoa") {
      setData((prev) => ({ ...prev, [name]: value }));
      validateCorpNameReflectOnSiSoa(value);
    } else {
      setData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleBlur = (field) => {
    if (field === "corporateName") {
      validateCorporateName(data.corporateName);
    } else if (field === "tinNumber") {
      validateTinNumber(data.tinNumber);
    } else if (field === "corpNameReflectOnSiSoa") {
      validateCorpNameReflectOnSiSoa(data.corpNameReflectOnSiSoa);
    }
  };

  const handleApplyToAllChange = (e) => {
    const isChecked = e.target.checked;
    const corpPrefix = data.corporateName.split("-")[0].trim() || data.corporateName;

    if (isChecked) {
      Swal.fire({
        title: "Are you sure?",
        html: `<div class="text-center text-base leading-6">
                 <p>Do you want to apply the selected changes to all accounts of</p>
                 <p><strong>Corp PREFIX: ${corpPrefix}</strong>?</p>
                 <p>You can choose which fields to update in the submenu.</p>
               </div>`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#ef4444",
        confirmButtonText: "Yes, apply to all!",
        cancelButtonText: "No, cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          setApplyToAll(true);
        } else {
          e.target.checked = false;
        }
      });
    } else {
      Swal.fire({
        title: "Notice",
        html: `<div class="text-center text-base leading-6">
                 <p>The changes you made will only apply to this corporate name:</p>
                 <p><strong>${data.corporateName}</strong>.</p>
               </div>`,
        icon: "info",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      }).then(() => {
        setApplyToAll(false);
        setApplyTinNumber(true);
        setApplyBillingAddress(true);
        setApplyCorpNameReflectOnSiSoa(true);
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isCorporateNameValid = validateCorporateName(data.corporateName);
    const isTinNumberValid = validateTinNumber(data.tinNumber);
    const isCorpNameReflectOnSiSoaValid = validateCorpNameReflectOnSiSoa(data.corpNameReflectOnSiSoa);

    if (!isCorporateNameValid || !isTinNumberValid || !isCorpNameReflectOnSiSoaValid) {
      Swal.fire({
        title: "Error!",
        html: `<div class="text-center text-base leading-6">
                 <p>Please correct the errors in the form before submitting.</p>
               </div>`,
        icon: "error",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      return;
    }

    const corpPrefix = data.corporateName.split("-")[0].trim() || data.corporateName;
    let noticeMessage = "";

    if (applyToAll) {
      const fieldsToUpdate = [];
      if (applyCorpNameReflectOnSiSoa) fieldsToUpdate.push("Corp Name Reflect on SI/SOA");
      if (applyTinNumber) fieldsToUpdate.push("TIN Number");
      if (applyBillingAddress) fieldsToUpdate.push("Billing Address");
      const fieldsText = fieldsToUpdate.length > 0 
        ? `The following field(s) will be updated for all accounts with <strong>Corp PREFIX: ${corpPrefix}</strong>:<br/><ul class="text-left mx-auto">${fieldsToUpdate.map(field => `<li>- ${field}</li>`).join('')}</ul>`
        : `No fields are selected to update for all accounts with <strong>Corp PREFIX: ${corpPrefix}</strong>.<br/>Only the current record will be updated.`;
      noticeMessage = fieldsText;
    } else {
      noticeMessage = `The changes will only apply to this corporate name:<br/><strong>${data.corporateName}</strong>.`;
    }

    Swal.fire({
      title: "Notice",
      html: `<div class="text-center text-base leading-6">${noticeMessage}</div>`,
      icon: "info",
      confirmButtonColor: "#2563eb",
      confirmButtonText: "Proceed",
    }).then((noticeResult) => {
      if (noticeResult.isConfirmed) {
        Swal.fire({
          title: "Are you sure?",
          html: `<div class="text-center text-base leading-6">
                   <p>Do you want to save the changes to this corporate record?</p>
                 </div>`,
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#2563eb",
          cancelButtonColor: "#ef4444",
          confirmButtonText: "Yes, save it!",
          cancelButtonText: "No, cancel",
        }).then(async (confirmResult) => {
          if (confirmResult.isConfirmed) {
            setSubmitting(true);
            const startTime = Date.now();

            try {
              await axios.put(
                `${BASE_URL}/corporate/${corp_code}`,
                {
                  corporateName: data.corporateName,
                  tinNumber: data.tinNumber,
                  corpNameReflectOnSiSoa: data.corpNameReflectOnSiSoa,
                  billingAddress: data.billingAddress,
                  applyToAll: applyToAll,
                  applyTinNumber: applyTinNumber,
                  applyBillingAddress: applyBillingAddress,
                  applyCorpNameReflectOnSiSoa: applyCorpNameReflectOnSiSoa,
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
                  },
                }
              );
              Swal.fire({
                title: "Updated!",
                html: `<div class="text-center text-base leading-6">
                         <p>Corporate details have been successfully updated.</p>
                       </div>`,
                icon: "success",
                confirmButtonColor: "#2563eb",
                confirmButtonText: "OK",
              });
            } catch (error) {
              if (error.response?.status === 401) window.location.href = "/login";
              if (error.response?.status === 403) openAdminAccessAlert();
              if (error.response?.status === 429) openRateLimitAlert();
              let err_msg = error.response?.data.message ?? `<p>Failed to update corporate details.</p><p>Please try again or contact support.</p>`

              Swal.fire({
                title: "Error!",
                html: `<div class="text-center text-base leading-6">${err_msg}</div>`,
                icon: "error",
                confirmButtonColor: "#2563eb",
                confirmButtonText: "OK",
              });
            } finally {
              const elapsedTime = Date.now() - startTime;
              const minLoadingTime = 1000;
              if (elapsedTime < minLoadingTime) {
                await new Promise((resolve) => setTimeout(resolve, minLoadingTime - elapsedTime));
              }
              setSubmitting(false);
            }
          }
        });
      }
    });
  };

  const handleBack = () => {
    navigate("/corporate-settings");
  };

  const handleSetup = () => {
    navigate(`/corporate-settings/setup/${corp_code}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 font-sans">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={handleBack}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Corporate Details</h1>
            <p className="text-sm text-gray-500">Manage corporate information</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          {loading ? (
            <div className="flex justify-center items-center h-48">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-600 text-xl" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Corporate Code
                  </label>
                  <input
                    type="text"
                    name="corporateCode"
                    value={data.corporateCode}
                    disabled
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Corporate Name
                  </label>
                  <input
                    disabled
                    type="text"
                    name="corporateName"
                    value={data.corporateName}
                    onChange={handleChange}
                    onBlur={() => handleBlur("corporateName")}
                    required
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                      errors.corporateName ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  {errors.corporateName && (
                    <p className="text-xs text-red-500 mt-1">{errors.corporateName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Corporate Prefix
                  </label>
                  <input
                    type="text"
                    name="corpPrefix"
                    value={data.corpPrefix}
                    disabled
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Actual Corp Name
                  </label>
                  <input
                    type="text"
                    name="actualCorpName"
                    value={data.actualCorpName}
                    disabled
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    TIN Number
                  </label>
                  <input
                    type="text"
                    name="tinNumber"
                    value={data.tinNumber}
                    onChange={handleChange}
                    onBlur={() => handleBlur("tinNumber")}
                    required
                    placeholder="e.g., 000-123-456-001"
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                      errors.tinNumber ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Format: 1-20 digits (e.g., 000-123-456)
                  </p>
                  {errors.tinNumber && (
                    <p className="text-xs text-red-500 mt-1">{errors.tinNumber}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Corp Name Reflect on SI/SOA
                  </label>
                  <textarea
                    rows="3"
                    name="corpNameReflectOnSiSoa"
                    value={data.corpNameReflectOnSiSoa}
                    onChange={handleChange}
                    onBlur={() => handleBlur("corpNameReflectOnSiSoa")}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                      errors.corpNameReflectOnSiSoa ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  {errors.corpNameReflectOnSiSoa && (
                    <p className="text-xs text-red-500 mt-1">{errors.corpNameReflectOnSiSoa}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-500 flex items-center">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        statusMap[data.status_flag]?.color || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {statusMap[data.status_flag]?.name || "Unknown"}
                    </span>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Address
                  </label>
                  <textarea
                    name="billingAddress"
                    value={data.billingAddress}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  />
                  <div className="mt-2 flex items-center">
                    <input
                      type="checkbox"
                      id="applyToAll"
                      checked={applyToAll}
                      onChange={handleApplyToAllChange}
                      className="w-4 h-4 text-blue-600 border-gray-200 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="applyToAll" className="ml-2 text-sm text-gray-700">
                      Apply changes to all accounts
                    </label>
                  </div>
                  {applyToAll && (
                    <div className="mt-2 ml-6 space-y-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="applyTinNumber"
                          checked={applyTinNumber}
                          onChange={(e) => setApplyTinNumber(e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-200 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="applyTinNumber" className="ml-2 text-sm text-gray-700">
                          TIN Number
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="applyCorpNameReflectOnSiSoa"
                          checked={applyCorpNameReflectOnSiSoa}
                          onChange={(e) => setApplyCorpNameReflectOnSiSoa(e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-200 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="applyCorpNameReflectOnSiSoa" className="ml-2 text-sm text-gray-700">
                          Corp Name Reflect on SI/SOA
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="applyBillingAddress"
                          checked={applyBillingAddress}
                          onChange={(e) => setApplyBillingAddress(e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-200 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="applyBillingAddress" className="ml-2 text-sm text-gray-700">
                          Billing Address
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleSetup}
                  className="px-3 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faCog} />
                  Setup Billing
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-3 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CorporateDetails;